import { useSelector } from 'react-redux'
import { AppState } from 'common/store'
import { Action } from 'redux'
import { storeCookie } from '../cookies/cookies'
import { createConsentString } from '../cookies/iab/iab-cookie'

export const CONSENT_COOKIE = 'consentLevels'
export const EU_CONSENT_COOKIE = 'euconsent'
export const CONSENT_TIMESTAMP_COOKIE = 'consentTimestamp'
export const CONSENT_COOKIE_EXPIRY = 180 // 6 months

/**
 * To check ConsentLevel use a bitwise test
 *
 * (consentLevel & ConsentLevel.Analytics) === ConsentLevel.Analytics if set
 *
 * Consent types - based on https://gdpr.eu/cookies/
 *
 * Essential:
 * Strictly necessary cookies — These cookies are essential for you to browse the website and use its features, such as accessing secure
 * areas of the site. Cookies that allow web shops to hold your items in your cart while you are shopping online are an example of strictly
 * necessary cookies. These cookies will generally be first-party session cookies. While it is not required to obtain consent for these
 * cookies, what they do and why they are necessary should be explained to the user.
 *
 * No level for this:
 * Preferences cookies — Also known as “functionality cookies,” these cookies allow a website to remember choices you have made in the past,
 * like what language you prefer, what region you would like weather reports for, or what your user name and password are so you can
 * automatically log in.
 *
 * Analytics (anonymous, no tracking):
 * Statistics cookies — Also known as “performance cookies,” these cookies collect information about how you use a website, like which pages
 * you visited and which links you clicked on. None of this information can be used to identify you. It is all aggregated and, therefore,
 * anonymized. Their sole purpose is to improve website functions. This includes cookies from third-party analytics services as long as the
 * cookies are for the exclusive use of the owner of the website visited.
 *
 * Advertising (targeted tracking):
 * Marketing cookies — These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many
 * times you see an ad. These cookies can share that information with other organizations or advertisers. These are persistent cookies and
 * almost always of third-party provenance.
 */
export const enum ConsentLevel {
    None = 0,
    Essential = 1,
    Analytics = 2,
    Advertising = 4,
    All = 1 | 2 | 4,
}

export type ConsentAction = never

export const LOAD_USER_CONSENT = 'LOAD_USER_CONSENT'
export const SHOW_COOKIE_SETTINGS_DIALOG = 'SHOW_COOKIE_SETTINGS_DIALOG'
export const ACCEPT_COOKIE_SETTINGS_DIALOG = 'ACCEPT_COOKIE_SETTINGS_DIALOG'

export interface LOAD_USER_CONSENT extends Action {
    type: typeof LOAD_USER_CONSENT
    payload: Pick<ConsentState, 'consentLevel' | 'consentTimestamp'>
}

export interface ConsentCookieState {
    consent: ConsentLevel
}

export interface ConsentTimestampCookieState {
    consentTimestamp: string
}

/**
 * IMPORTANT: This should only be used during SSR
 *
 * This is because we can't reload scripts which are loaded so the value should not be updated client side
 **/
export function loadConsent(
    consentLevel: ConsentLevel,
    consentTimestamp?: string
): LOAD_USER_CONSENT {
    return {
        type: LOAD_USER_CONSENT,
        payload: { consentLevel, consentTimestamp },
    }
}
export interface SHOW_COOKIE_SETTINGS_DIALOG extends Action {
    type: typeof SHOW_COOKIE_SETTINGS_DIALOG
    payload: boolean
}

export const showCookieSettingsDialog = (payload = true): SHOW_COOKIE_SETTINGS_DIALOG => ({
    type: SHOW_COOKIE_SETTINGS_DIALOG,
    payload,
})

export interface ACCEPT_COOKIE_SETTINGS_DIALOG extends Action {
    type: typeof ACCEPT_COOKIE_SETTINGS_DIALOG
    payload: Required<Pick<ConsentState, 'consentLevel'>>
}

export const acceptCookieSettingsDialog = (
    payload: Required<Pick<ConsentState, 'consentLevel'>>
): ACCEPT_COOKIE_SETTINGS_DIALOG => ({
    type: ACCEPT_COOKIE_SETTINGS_DIALOG,
    payload,
})

/**
 *  Once consentLevel is set, we don't reset it, so that way scripts accessing it don't update when the consent level changes.
 **/
export interface ConsentState {
    /**
     * IMPORTANT: consentLevel should not be updated except during server side render
     *
     * This is because we can't reload scripts which are loaded so the value should not be updated client side
     **/
    consentLevel?: ConsentLevel
    showConsentDialog?: boolean
    consentTimestamp?: string
}

const defaultState: ConsentState = { showConsentDialog: true, consentTimestamp: undefined }

type ConsentActions =
    | SHOW_COOKIE_SETTINGS_DIALOG
    | ACCEPT_COOKIE_SETTINGS_DIALOG
    | LOAD_USER_CONSENT
    | { type: '@@INIT' /** This represents all other actions */ }

export function consentReducer(state: ConsentState = defaultState, action: ConsentActions) {
    switch (action.type) {
        case LOAD_USER_CONSENT: {
            const { consentLevel, consentTimestamp } = action.payload

            return {
                ...state,
                consentLevel,
                consentTimestamp,
                showConsentDialog: !consentLevel,
            }
        }
        case SHOW_COOKIE_SETTINGS_DIALOG: {
            return {
                ...state,
                showConsentDialog: action.payload,
            }
        }
        case ACCEPT_COOKIE_SETTINGS_DIALOG: {
            const { consentLevel } = action.payload

            storeConsentCookies(consentLevel)

            return {
                ...state,
                consentLevel,
                showConsentDialog: false,
            }
        }
        default:
            return state
    }
}

export function useConsentState(): ConsentState {
    const consent = useSelector<AppState, ConsentState>(state => state.consent)
    return consent
}

export function storeConsentCookies(consent: ConsentLevel) {
    storeCookie<ConsentCookieState>(
        CONSENT_COOKIE,
        {
            consent,
        },
        {
            expires: CONSENT_COOKIE_EXPIRY,
        }
    )

    storeCookie<ConsentTimestampCookieState>(
        CONSENT_TIMESTAMP_COOKIE,
        {
            consentTimestamp: new Date().toISOString(),
        },
        {
            expires: CONSENT_COOKIE_EXPIRY,
        }
    )

    storeCookie(EU_CONSENT_COOKIE, createConsentString(consent), {
        expires: CONSENT_COOKIE_EXPIRY,
    })
}
